import { useState } from 'react'
import { ChevronDown, Filter, X } from 'lucide-react'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { cn } from '@/shared/lib/utils'

interface FilterOption {
  value: string
  label: string
  count?: number
}

interface FilterConfig {
  campo: string
  label: string
  icon?: React.ReactNode
  options: FilterOption[]
  placeholder?: string
}

interface FilterPanelProps {
  filters: FilterConfig[]
  activeFilters: Record<string, string[]>
  onFilterChange: (campo: string, values: string[]) => void
  onClearFilters: () => void
  isLoading?: boolean
}

export function FilterPanel({
  filters,
  activeFilters,
  onFilterChange,
  onClearFilters,
  isLoading = false,
}: FilterPanelProps) {
  const [isOpen, setIsOpen] = useState(false)

  const activeFilterCount = Object.values(activeFilters).reduce(
    (acc, values) => acc + values.length,
    0
  )

  const handleSelectChange = (campo: string, value: string) => {
    if (value === 'all') {
      onFilterChange(campo, [])
    } else {
      const currentValues = activeFilters[campo] || []
      if (currentValues.includes(value)) {
        onFilterChange(campo, currentValues.filter((v) => v !== value))
      } else {
        onFilterChange(campo, [...currentValues, value])
      }
    }
  }

  const removeFilter = (campo: string, value: string) => {
    const currentValues = activeFilters[campo] || []
    onFilterChange(campo, currentValues.filter((v) => v !== value))
  }

  return (
    <Card className="border border-gray-300 dark:border-gray-700 w-full">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between py-2 px-3 cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center gap-1.5">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <h3 className="text-sm font-semibold">Filtros</h3>
              {activeFilterCount > 0 && (
                <Badge variant="default" className="bg-[#2fbc80] hover:bg-[#2fbc80]/90 text-xs h-5">
                  {activeFilterCount}
                </Badge>
              )}
            </div>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform duration-200',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-3 px-3">
            <div className="border-t pt-3">
              {/* Active Filters Tags */}
              {activeFilterCount > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {Object.entries(activeFilters).map(([campo, values]) =>
                    values.map((value) => (
                      <Badge
                        key={`${campo}-${value}`}
                        variant="secondary"
                        className="gap-1 pr-1 text-xs"
                      >
                        {value}
                        <button
                          onClick={() => removeFilter(campo, value)}
                          className="ml-1 rounded-full hover:bg-muted p-0.5"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </Badge>
                    ))
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClearFilters}
                    className="h-5 text-xs text-muted-foreground hover:text-destructive px-2"
                  >
                    Limpiar
                  </Button>
                </div>
              )}

              {/* Filter Selects */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full">
                {filters.map((filter) => (
                  <div key={filter.campo} className="space-y-1 min-w-0">
                    <label className="text-xs font-medium flex items-center gap-1.5 text-muted-foreground">
                      {filter.icon}
                      {filter.label}
                    </label>
                    <Select
                      disabled={isLoading}
                      value={activeFilters[filter.campo]?.[0] || 'all'}
                      onValueChange={(value) => handleSelectChange(filter.campo, value)}
                    >
                      <SelectTrigger className="w-full h-8 text-xs">
                        <SelectValue placeholder={filter.placeholder || `Todos`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all" className="text-xs">
                          {filter.placeholder || `Todos`}
                        </SelectItem>
                        {filter.options.map((option) => (
                          <SelectItem key={option.value} value={option.value} className="text-xs">
                            <span className="flex items-center justify-between w-full">
                              <span>{option.label}</span>
                              {option.count !== undefined && (
                                <span className="text-muted-foreground text-xs ml-2">
                                  ({option.count})
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
